<template>
  <div class="work-type-container">
    <!-- 查询条件 -->
    <el-card class="search-wrapper">
      <div slot="header" class="flex justify-between">
        <span>{{ $t("workType.search.title") }}</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input
            v-model.trim="searchForm.typeName"
            :placeholder="$t('workType.search.typeName')"
            clearable
          />
        </el-col>
        <el-col :span="6">
          <el-button type="primary" @click="handleSearch">
            <i class="el-icon-search"></i>
            {{ $t("common.search") }}
          </el-button>
          <el-button @click="handleReset">
            <i class="el-icon-refresh"></i>
            {{ $t("common.reset") }}
          </el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 工作单类型列表（添加+标志 + 无多余箭头） -->
    <el-card class="list-wrapper">
      <div slot="header" class="flex justify-between">
        <span>{{ $t("workType.list.title") }}</span>
        <el-button
          type="primary"
          size="small"
          style="float: right"
          @click="handleAdd"
        >
          <i class="el-icon-plus"></i>
          {{ $t("common.add") }}
        </el-button>
      </div>

      <!-- 核心优化：添加+标志 + 表格宽度100% -->
      <el-table
        v-loading="loading"
        :data="flattenTableData"
        border
        style="width: 100% !important"
        row-key="wtId"
        :span-method="objectSpanMethod"
        ref="workTypeTable"
        highlight-current-row
        :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
      >
        <!-- 类型名称列 - 添加+/-标志（无多余箭头） -->
        <el-table-column label="类型名称" align="left" width="300">
          <template slot-scope="scope">
            <!-- 父节点：显示+/-标志 + 文本 -->
            <span
              v-if="scope.row.isParent"
              class="parent-row-content"
              @click.stop="toggleExpand(scope.row)"
            >
              <!-- +/- 展开/收起标志 -->
              <span
                class="expand-icon"
                :class="{ expanded: expandedKeys.includes(scope.row.wtId) }"
              >
                {{ expandedKeys.includes(scope.row.wtId) ? "-" : "+" }}
              </span>
              <span class="parent-name">{{ scope.row.typeName }}</span>
            </span>
            <!-- 子节点：仅缩进+文本，无任何箭头 -->
            <span v-else class="child-node">
              {{ scope.row.typeName }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="扣款" align="center" width="100">
          <template slot-scope="scope">
            {{ scope.row.deduction === "Y" ? "是" : "否" }}
          </template>
        </el-table-column>

        <el-table-column label="通知方式" align="center" width="120">
          <template slot-scope="scope">
            <span v-if="scope.row.smsWay === 'WECHAT'">微信</span>
            <span v-else-if="scope.row.smsWay === 'ALI_SMS'">阿里云短信</span>
            <span v-else-if="scope.row.smsWay === 'WORK_LICENSE'"
              >员工工牌</span
            >
            <span v-else>未知</span>
          </template>
        </el-table-column>

        <el-table-column
          label="创建时间"
          prop="createTime"
          align="center"
          width="180"
        />

        <el-table-column label="备注" align="center" width="300">
          <template slot-scope="scope">
            {{ scope.row.remark || "-" }}
          </template>
        </el-table-column>

        <el-table-column label="操作" align="center" width="280" fixed="right">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="primary"
              @click="handleEdit(scope.row)"
            >
              编辑
            </el-button>
            <el-button
              size="mini"
              type="danger"
              @click="handleDelete(scope.row)"
              :disabled="
                scope.row.isParent &&
                scope.row.children &&
                scope.row.children.length > 0
              "
            >
              删除
            </el-button>
            <el-button
              size="mini"
              type="success"
              @click="handleAddChild(scope.row)"
              :disabled="!scope.row.isParent"
            >
              <i class="el-icon-plus"></i>
              添加子类型
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        :current-page.sync="page.current"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="page.size"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        style="width: 100%; text-align: right; margin-top: 10px"
      />
    </el-card>

    <!-- 组件 -->
    <add-work-type
      ref="addWorkType"
      @success="handleSuccess"
      :root-type-list="rootTypeList"
    />
    <edit-work-type ref="editWorkType" @success="handleSuccess" />
    <delete-work-type ref="deleteWorkType" @success="handleSuccess" />
  </div>
</template>

<script>
import { listWorkType } from "@/api/oa/workTypeApi";
import { getCommunityId } from "@/api/community/communityApi";
import AddWorkType from "@/components/oa/addWorkType";
import EditWorkType from "@/components/oa/editWorkType";
import DeleteWorkType from "@/components/oa/deleteWorkType";

export default {
  name: "WorkTypeList",
  components: {
    AddWorkType,
    EditWorkType,
    DeleteWorkType,
  },
  data() {
    return {
      loading: false,
      searchForm: {
        typeName: "",
      },
      originalTableData: [], // 原始树形数据
      page: {
        current: 1,
        size: 10,
        total: 0,
      },
      communityId: "",
      rootTypeList: [],
      expandedKeys: [], // 展开的父节点ID（初始为空，默认不展开）
      flattenTableData: [], // 扁平化后的表格数据（父子节点在同一列表）
    };
  },
  created() {
    this.communityId = getCommunityId();
    this.getList();
  },
  methods: {
    async getList() {
      try {
        this.loading = true;
        const params = {
          page: this.page.current,
          row: this.page.size,
          typeName: this.searchForm.typeName,
          communityId: this.communityId,
        };
        const response = await listWorkType(params);
        const rawData = response.data || [];
        this.page.total = response.total || 0;

        // 处理原始数据：标记父节点
        this.originalTableData = rawData.map((item) => ({
          ...item,
          isParent: true, // 标记为父节点
          children: Array.isArray(item.children)
            ? item.children.map((child) => ({
                ...child,
                isParent: false, // 标记为子节点
                parentId: item.wtId, // 关联父节点ID
              }))
            : [],
        }));

        // 提取一级类型列表
        this.rootTypeList = this.originalTableData.map((item) => ({
          wtId: item.wtId,
          typeName: item.typeName,
        }));

        // 扁平化数据：子节点紧跟父节点（初始无展开，所以子节点不显示）
        this.flattenTableData = this.buildFlattenData();
      } catch (error) {
        this.$message.error("获取数据失败");
        console.error("获取工作单类型失败：", error);
      } finally {
        this.loading = false;
      }
    },

    // 构建扁平化数据：子节点紧跟对应父节点（仅展开的父节点显示子节点）
    buildFlattenData() {
      let result = [];

      this.originalTableData.forEach((parent) => {
        // 添加父节点
        result.push(parent);

        // 仅当父节点被手动展开时，才添加子节点
        if (
          this.expandedKeys.includes(parent.wtId) &&
          parent.children.length > 0
        ) {
          result = [...result, ...parent.children];
        }
      });

      return result;
    },

    // 切换父节点展开/收起（点击父节点区域触发）
    toggleExpand(parentRow) {
      const index = this.expandedKeys.indexOf(parentRow.wtId);
      if (index > -1) {
        // 收起：移除展开的ID，显示+
        this.expandedKeys.splice(index, 1);
      } else {
        // 展开：添加展开的ID，显示-
        this.expandedKeys.push(parentRow.wtId);
      }
      // 重新构建扁平化数据（控制子节点显示/隐藏）
      this.flattenTableData = this.buildFlattenData();
    },

    // 合并单元格（可选，优化视觉效果）
    objectSpanMethod() {
      // 子节点可以根据需要合并单元格，这里暂不合并，只做层级显示
      return {
        rowspan: 1,
        colspan: 1,
      };
    },

    handleSearch() {
      this.page.current = 1;
      this.getList();
    },
    handleReset() {
      this.searchForm = {
        typeName: "",
      };
      this.getList();
    },
    handleAdd() {
      this.$refs.addWorkType.open({
        parentId: "",
        parentName: "",
      });
    },
    handleAddChild(row) {
      this.$refs.addWorkType.open({
        parentId: row.wtId,
        parentName: row.typeName,
      });
    },
    handleEdit(row) {
      this.$refs.editWorkType.open(row);
    },
    handleDelete(row) {
      if (row.isParent && row.children && row.children.length > 0) {
        this.$message.warning("该类型下存在子类型，无法删除！");
        return;
      }
      this.$refs.deleteWorkType.open(row);
    },
    handleSuccess() {
      this.getList();
    },
    handleSizeChange(val) {
      this.page.size = val;
      this.getList();
    },
    handleCurrentChange(val) {
      this.page.current = val;
      this.getList();
    },
  },
};
</script>

<style lang="scss" scoped>
.work-type-container {
  padding: 20px;
  width: 100%; // 容器满宽

  .search-wrapper {
    margin-bottom: 20px;
    width: 100%;
  }

  .list-wrapper {
    margin-bottom: 20px;
    width: 100%;
  }

  // 强制表格满宽
  ::v-deep .el-table {
    width: 100% !important;
  }

  // 表格容器无滚动（确保满宽）
  ::v-deep .el-table__header-wrapper,
  ::v-deep .el-table__body-wrapper {
    width: 100% !important;
    overflow-x: hidden !important;
  }

  // 隐藏Element UI自带的树形箭头（关键！）
  ::v-deep .el-table__expand-icon {
    display: none !important;
  }

  // 父节点内容容器（点击可展开）
  .parent-row-content {
    display: inline-flex;
    align-items: center;
    width: 100%;
    cursor: pointer; // 鼠标悬浮显示手型，提示可点击
    padding: 2px 0;
  }

  // +/- 展开/收起标志样式
  .expand-icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    line-height: 20px;
    text-align: center;
    border-radius: 2px;
    background-color: #e8f4ff;
    color: #409eff;
    font-size: 14px;
    font-weight: bold;
    margin-right: 8px;
    flex-shrink: 0;
    transition: all 0.2s ease;

    // 展开状态（-）的样式
    &.expanded {
      background-color: #409eff;
      color: #fff;
    }

    // hover效果
    &:hover {
      background-color: #409eff;
      color: #fff;
      transform: scale(1.1);
    }
  }

  // 父节点名称样式
  .parent-name {
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    transition: color 0.2s ease;
  }

  // 父节点hover时名称变色
  .parent-row-content:hover .parent-name {
    color: #409eff;
  }

  // 子节点：仅缩进，无任何箭头
  .child-node {
    font-size: 14px;
    color: #4b5563;
    display: inline-block;
    padding-left: 28px; // 缩进匹配+标志宽度，区分层级
  }

  // 分页栏满宽
  .el-pagination {
    width: 100%;
    text-align: right;
    margin-top: 10px;
  }

  // 表格行hover效果优化
  ::v-deep .el-table__row:hover {
    background-color: #f9fafb !important;
  }

  // 父节点行背景
  ::v-deep .el-table__row:nth-child(odd):not(.el-table__row--striped) {
    background-color: #f5f7fa;
  }
}
</style>
